import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Search, Eye, EyeOff, Loader2, Users, X, Lock, Phone, Mail, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { vendorsApi } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20";
const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", city: "", locations: [], gstin: "", pan: "", category: "", payment_terms: "", bank_details: "", notes: "", active: true };

export default function VendorsManage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setVendors(await vendorsApi.list(q ? { q } : {})); }
    catch { toast.error("Failed to load vendors"); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const del = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try { await vendorsApi.remove(v.id); toast.success("Vendor deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const toggleActive = async (v) => {
    try { await vendorsApi.update(v.id, { ...v, active: !v.active }); load(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <button onClick={() => navigate("/admin/services")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to services</button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Marketplace</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold">Vendor Management</h1>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Admin-only. Costs & bank details are never shown to customers.</p>
          </div>
          <button onClick={() => setEditing(EMPTY)} data-testid="new-vendor-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg"><Plus className="h-4 w-4" /> New Vendor</button>
        </div>

        <div className="relative mt-8">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="vendors-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors…" className="w-full rounded-full border border-input bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20" />
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-secondary/60" />)}</div>
          ) : vendors.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
              <Users className="mx-auto h-10 w-10 text-gold" />
              <h3 className="mt-4 font-serif text-2xl font-medium">No vendors yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Add vendors so you can assign them to services and track costs.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="vendors-list">
              {vendors.map((v) => (
                <div key={v.id} data-testid={`vendor-row-${v.id}`} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-muted-foreground">{v.code}</span>
                      {!v.active && <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-xs text-zinc-600">inactive</span>}
                      {v.category && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{v.category}</span>}
                    </div>
                    <h3 className="mt-0.5 font-serif text-lg font-semibold">{v.name}</h3>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {v.contact_person && <span>{v.contact_person}</span>}
                      {v.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{v.phone}</span>}
                      {v.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{v.email}</span>}
                      {v.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{v.city}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleActive(v)} data-testid={`vendor-toggle-${v.id}`} className="rounded-xl border border-border p-2.5 hover:bg-secondary">{v.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button onClick={() => setEditing(v)} data-testid={`vendor-edit-${v.id}`} className="rounded-xl border border-border p-2.5 hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(v)} data-testid={`vendor-delete-${v.id}`} className="rounded-xl border border-border p-2.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && <VendorModal value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VendorModal({ value, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...value, locations: (value.locations || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Vendor name is required"); return; }
    setBusy(true);
    const payload = { ...f, locations: f.locations.split(",").map((x) => x.trim()).filter(Boolean) };
    delete payload.code; delete payload.id; delete payload.created_at; delete payload.documents;
    try {
      if (value.id) await vendorsApi.update(value.id, payload);
      else await vendorsApi.create(payload);
      toast.success("Vendor saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-8" onClick={onClose} data-testid="vendor-modal">
      <div className="w-full max-w-2xl rounded-2xl bg-card p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-serif text-xl font-semibold">{value.id ? "Edit vendor" : "New vendor"}</h3><button onClick={onClose} className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button></div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input data-testid="vendor-name" className={inputCls} placeholder="Vendor / business name *" value={f.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="vendor-category" className={inputCls} placeholder="Vendor category (e.g. Caterer)" value={f.category} onChange={(e) => set("category", e.target.value)} />
          <input data-testid="vendor-contact" className={inputCls} placeholder="Contact person" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
          <input data-testid="vendor-phone" className={inputCls} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          <input data-testid="vendor-email" className={inputCls} placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} />
          <input data-testid="vendor-city" className={inputCls} placeholder="City" value={f.city} onChange={(e) => set("city", e.target.value)} />
          <input data-testid="vendor-locations" className={`${inputCls} sm:col-span-2`} placeholder="Service locations (comma separated)" value={f.locations} onChange={(e) => set("locations", e.target.value)} />
          <input data-testid="vendor-address" className={`${inputCls} sm:col-span-2`} placeholder="Business address" value={f.address} onChange={(e) => set("address", e.target.value)} />
        </div>

        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-amber-800"><Lock className="h-3.5 w-3.5" /> Confidential — admin only</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="vendor-gstin" className={inputCls} placeholder="GSTIN" value={f.gstin} onChange={(e) => set("gstin", e.target.value)} />
            <input data-testid="vendor-pan" className={inputCls} placeholder="PAN" value={f.pan} onChange={(e) => set("pan", e.target.value)} />
            <input data-testid="vendor-terms" className={inputCls} placeholder="Payment terms" value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} />
            <input data-testid="vendor-bank" className={inputCls} placeholder="Bank / payment details" value={f.bank_details} onChange={(e) => set("bank_details", e.target.value)} />
            <textarea data-testid="vendor-notes" rows={2} className={`${inputCls} sm:col-span-2`} placeholder="Internal notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--gold))]" /> Active vendor</label>
        <button onClick={submit} disabled={busy} data-testid="vendor-save-btn" className="mt-5 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Save vendor"}</button>
      </div>
    </div>
  );
}
